import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PlaneLanding, PlaneTakeoff, ClipboardList, Send, AlertTriangle, Download, FlaskConical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { TomorrowArrivals } from "@/components/TomorrowArrivals";
import { api, download, errMsg, fmtDate, todayIso } from "@/lib/api";

function Stat({ icon: Icon, label, value, tone = "text-stone-900", testId, to }) {
  return (
    <Link to={to} className="rounded-2xl border bg-white p-5 transition-transform hover:-translate-y-0.5" data-testid={testId}>
      <Icon className="h-5 w-5 text-primary" />
      <p className={`mt-4 font-display text-4xl font-extrabold ${tone}`}>{value}</p>
      <p className="text-xs font-semibold uppercase tracking-wide text-stone-500 mt-1">{label}</p>
    </Link>
  );
}

function BList({ title, items, empty, testId }) {
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid={testId}>
      <h3 className="font-display text-lg font-bold mb-3">{title}</h3>
      {items.length === 0 ? <p className="text-sm text-stone-500">{empty}</p> : (
        <ul className="divide-y">
          {items.map((b) => (
            <li key={b.id}>
              <Link to={`/prenotazioni/${b.id}`} className="flex items-center justify-between gap-3 py-3 hover:text-primary" data-testid={`${testId}-item-${b.id}`}>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{b.ref_name || "Senza nome"}</p>
                  <p className="text-xs text-stone-500">{b.room_name} · {fmtDate(b.arrival)} → {fmtDate(b.departure)}</p>
                </div>
                <StatusBadge kind="booking" status={b.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [from, setFrom] = useState(todayIso().slice(0, 8) + "01");
  const [to, setTo] = useState(todayIso());
  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!d) return <p className="text-stone-500" data-testid="dashboard-loading">Caricamento…</p>;
  const exportCsv = () => download("/export/ospiti.csv", { date_from: from, date_to: to }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="fade-up">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">Oggi · {fmtDate(d.today)}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">Buongiorno.</h1>
      </div>
      {d.alloggiati_mode !== "real" && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="mock-mode-banner">
          <FlaskConical className="h-5 w-5 shrink-0" />
          <p><b>Alloggiati Web in modalità prova (MOCK):</b> nessuna schedina viene inviata davvero alla Polizia di Stato. Inserisci le credenziali e attiva la modalità reale in Impostazioni.</p>
        </div>
      )}
      {d.tables_sample && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" data-testid="sample-tables-banner">
          Tabelle comuni/stati/documenti <b>di esempio</b> (poche voci): scarica le tabelle ufficiali da Impostazioni → Tabelle codici dopo aver inserito le credenziali.
        </div>
      )}
      {d.late.length > 0 && (
        <Link to="/schedine" className="flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900" data-testid="late-schedine-alert">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p><b>{d.late.length === 1 ? "1 schedina oltre le 24 ore dall'arrivo" : `${d.late.length} schedine oltre le 24 ore dall'arrivo`}</b> non ancora inviat{d.late.length === 1 ? "a" : "e"}: {d.late.slice(0, 4).map((l) => l.name).join(", ")}{d.late.length > 4 ? "…" : ""}</p>
        </Link>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        <Stat icon={PlaneLanding} label="Arrivi oggi" value={d.arrivals.length} testId="stat-arrivals" to="/prenotazioni?view=arrivi" />
        <Stat icon={PlaneTakeoff} label="Partenze oggi" value={d.departures.length} testId="stat-departures" to="/prenotazioni?view=partenze" />
        <Stat icon={ClipboardList} label="Check-in mancanti" value={d.incomplete.length} testId="stat-incomplete" to="/prenotazioni?view=incompleti" />
        <Stat icon={Send} label="Schedine da inviare" value={d.to_send} testId="stat-to-send" to="/schedine" />
        <Stat icon={AlertTriangle} label="Errori di invio" value={d.errors} tone={d.errors ? "text-rose-700" : "text-stone-900"} testId="stat-errors" to="/schedine?status=errore" />
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <Link to={`/movimentazione?m=${d.istat_prev.month}`} className="rounded-2xl border bg-stone-900 text-white p-6 lg:col-span-1" data-testid="istat-prev-card">
          <p className="text-xs uppercase tracking-widest text-white/60">ISTAT · DMS Puglia</p>
          <p className="font-display text-2xl font-bold mt-2">Movimentazione {d.istat_prev.month}</p>
          <div className="mt-3"><StatusBadge kind="month" status={d.istat_prev.status} testId="istat-prev-status" /></div>
          <p className="text-sm text-white/70 mt-3">
            {d.istat_prev.status === "trasmesso" ? "Trasmissione registrata." : `Scadenza trasmissione: ${fmtDate(d.istat_prev.deadline)}`}
            {d.istat_prev.status !== "trasmesso" && d.today > d.istat_prev.deadline && <span className="block mt-1 font-semibold text-rose-300" data-testid="istat-overdue">Scadenza superata: trasmetti al più presto</span>}
          </p>
          {d.istat_prev.modified && <p className="text-sm text-amber-300 mt-2">Dati modificati dopo l'export/trasmissione</p>}
          <p className="text-xs text-white/50 mt-4">Mese in corso ({d.istat_current.month}): <span className="text-white">{d.istat_current.status.replace("_", " ")}</span></p>
        </Link>
        <BList title="Arrivi di oggi" items={d.arrivals} empty="Nessun arrivo oggi" testId="today-arrivals" />
        <BList title="Partenze di oggi" items={d.departures} empty="Nessuna partenza oggi" testId="today-departures" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <TomorrowArrivals items={d.tomorrow} template={d.arrival_template} />
        <BList title="Check-in incompleti (prossimi 7 giorni)" items={d.incomplete} empty="Tutti i check-in sono completi" testId="incomplete-list" />
        <div className="rounded-2xl border bg-white p-5" data-testid="export-guests-card">
          <h3 className="font-display text-lg font-bold">Export ospiti per periodo</h3>
          <p className="text-sm text-stone-500 mb-4">CSV per contabilità e imposta di soggiorno.</p>
          <div className="grid grid-cols-2 gap-3">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="export-guests-from" />
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="export-guests-to" />
          </div>
          <Button className="mt-4" variant="outline" onClick={exportCsv} data-testid="export-guests-button"><Download className="h-4 w-4 mr-2" />Scarica CSV</Button>
        </div>
      </div>
    </div>
  );
}
